/**
 * Consent-envelope emission (MTN-1439).
 *
 * Serializes the versioned consent envelope — the canonical record of Termly
 * consent state defined in @moneytalksnews/privacy-shared — into the
 * mtn-consent-envelope parent-domain session cookie on every Termly update,
 * so edge consumers (MTN-Tally click capture, and MTN-Tune-Postback through
 * the click record) can distinguish a valid US default-on record from "no
 * trustworthy consent evidence" (MTN-1434 P1-2).
 *
 * Emission points:
 * - once at init, before Termly resolves — a navigation in that window
 *   carries an honest `unresolved` envelope instead of the previous page's
 *   stale state (audit state 12);
 * - on every termlyDataUpdate, with a bumped generation counter;
 * - when Termly's loader fails or the resolution deadline passes.
 *
 * The module also carries the runtime tripwire for Termly's undocumented
 * do_not_sell coupling contract (MTN-1439 consequence 2):
 * - a resolved record grants a non-essential category alongside
 *   do_not_sell: true (the coupling invariant that makes the server-side
 *   category lookup a faithful do-not-sell proxy) → PostHog error
 *   "termly_do_not_sell_coupling_violation". Edge consumers must enforce
 *   the envelope's explicit doNotSell field rather than infer it from the
 *   categories.
 */

import {
  buildConsentEnvelope,
  CONSENT_ENVELOPE_COOKIE,
  violatesDoNotSellCoupling,
} from "@moneytalksnews/privacy-shared";
import { setIfChanged } from "./cookies";
import { captureMessage } from "./posthog";
import { onUnavailable } from "./termly";

// Matches the PostHog wrapper's own Termly deadline: after this long with no
// Termly result the envelope reports `failed` rather than `unresolved`.
const RESOLVE_TIMEOUT_MS = 8000;

let generation = 0;
let loadFailed = false;
let firedCouplingTripwire = false;

/**
 * Termly's own stored consent record, which carries fields
 * getConsentState() omits (do_not_sell, gpc, event_type,
 * template_version_id). Vendor
 * localStorage read defensively — absence or a shape change degrades the
 * envelope's optional fields to null instead of breaking emission.
 */
const readTermlyConsentRecord = () => {
  try {
    const cache = JSON.parse(
      window.localStorage.getItem("TERMLY_API_CACHE") || "{}"
    );
    const record = cache?.TERMLY_COOKIE_CONSENT?.value;
    if (!record || typeof record !== "object" || Array.isArray(record)) {
      return null;
    }
    if (typeof record.do_not_sell === "boolean") {
      return record;
    }
    return { ...record, do_not_sell: undefined };
  } catch {
    return null;
  }
};

const fireTripwires = (envelope) => {
  if (!firedCouplingTripwire && violatesDoNotSellCoupling(envelope)) {
    firedCouplingTripwire = true;
    captureMessage("termly_do_not_sell_coupling_violation", {
      categories: JSON.stringify(envelope.categories),
      source: "consent-envelope",
    });
    // A coupling breach may leave Analytics granted or denied. Mirror the
    // alarm onto the always-on identifier-free Plausible baseline (MTN-334)
    // so it stays audible in either case.
    window.mtnPlausible?.("Termly Coupling Violation");
  }
};

const emit = () => {
  const envelope = buildConsentEnvelope({
    consentRecord: readTermlyConsentRecord(),
    generation,
    gpcSignal: navigator.globalPrivacyControl === true,
    loadFailed,
    observedAt: new Date().toISOString(),
    regionSettings: window.termlyRegionSettings,
    termlyData: window.termlyData,
  });

  // set() URI-encodes the value, so the wire format equals
  // serializeConsentEnvelope(envelope) and parseConsentEnvelope reads it
  // back directly (locked by the wire-format test). Session-lifetime on
  // purpose: consent evidence should not outlive the browsing session —
  // the edge re-checks via mtn-termly-uuid for anything longer-lived.
  setIfChanged(CONSENT_ENVELOPE_COOKIE, JSON.stringify(envelope), {
    parent: true,
  });

  fireTripwires(envelope);
};

/**
 * Start envelope emission. Called once from main.js; listeners live for the
 * page's lifetime.
 */
export const initConsentEnvelope = () => {
  document.addEventListener("termlyDataUpdate", () => {
    generation += 1;
    emit();
  });

  // Region settings arrive on Termly's `initialized` event (stashed by
  // head.blade.php), usually between the unresolved init emission and the
  // first consent event. Metadata only — the generation counts consent
  // changes, so it does not bump here.
  document.addEventListener("termlyRegionSettings", emit);

  // Loader failure or the resolution deadline — one settled callback,
  // canceled if Termly resolves first (util/termly.js). Late resolution
  // after a failure still recovers: termlyDataUpdate above re-emits.
  onUnavailable(
    () => {
      loadFailed = true;
      emit();
    },
    { timeoutMs: RESOLVE_TIMEOUT_MS }
  );

  emit();
};
